import { UseQueryOptions, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  AccountTicketDetails,
  BestTicketBlockDetails,
  EventAvailabilityDetails,
  GroupDetails,
  PlanPackageComponentDetails,
  PremiumInventoryDetails,
  ReservationUpdateCartDetails,
  SectionDetails,
  TicketCartDetails,
  TicketDetails,
  TransferDetails,
} from "features/types";
import { get, patch, post } from "services/http";

export interface ReserveSeatsParams {
  accountId: number;
  planPackageId: number;
  eventId: number;
  section: string;
  row: string;
}

export interface ReserveParams extends ReserveSeatsParams {
  userIds: number[];
}

export interface RedeemParams extends ReserveSeatsParams {
  partySize: number;
}

export interface CalculateChargeItem {
  planPackageId: number | string;
  planPackageComponentId: number | string;
  ticketId: number | string;
}

export interface CalculateGroupChargeItem {
  planPackageId: number | string;
  planPackageComponentId: number | string;
}

export interface ReserveOrRedeemItem {
  planPackageComponentId: number | string;
  userId?: number | string | null;
}

export interface GroupReserveOrRedeemItem {
  planPackageComponentId: number;
  userId?: number;
}

interface Selection {
  section: string;
  row: string;
  seat: string;
}
export interface HoldTicketRequest {
  planPackageId: number;
  eventId: number;
  partySize: number;
  premiumOnly: boolean;
  selections: Selection[];
}

export type FetchBestBlocksRequest = {
  eventId?: number;
  planPackageId?: number;
  partySize: number;
  sections?: string[];
  premiumOnly: boolean;
};

export interface CalculateTicketChargeItem {
  planPackageId: number;
  planPackageComponentId: number;
  ticketId: number;
}
interface CalculateReservationUpdateChargesRequest {
  accountId: number;
  eventId: number;
  newTicketItems: CalculateTicketChargeItem[];
  ticketRefundIds: number[];
}

interface HoldTicketsForReservationUpdateRequest {
  accountId: number;
  planPackageId: number;
  eventId: number;
  selections: Selection[];
  premiumOnly: boolean;
  groupCode: string;
}

interface ReserveTicketsForReservationUpdateRequest {
  accountId: number;
  planPackageId: number;
  eventId: number;
  groupCode: string;
  items: ReserveOrRedeemItem[];
  releaseTicketIds: number[];
}

export const fetchSectionsRequest = (params: {
  eventId?: number;
  planPackageId?: number;
  partySize: number;
}) => {
  return get({
    url: "/tickets/sections",
    params: {
      ...params,
    },
  }) as Promise<SectionDetails[]>;
};
export const fetchBestBlocksRequest = (params: FetchBestBlocksRequest) => {
  return get({
    url: "/tickets/best_blocks",
    params,
  }) as Promise<BestTicketBlockDetails[]>;
};

export const useFetchEventAvailablitiesQuery = ({
  accountId,
  planPackageId,
}: {
  accountId: number | undefined;
  planPackageId: number | undefined;
}) => {
  return useQuery(
    ["fetchEventAvailablities", planPackageId],
    () => {
      return get({
        url: "/tickets/events",
        params: {
          accountId,
          planPackageId,
        },
      }) as Promise<Array<EventAvailabilityDetails>>;
    },
    {
      enabled: false,
    }
  );
};

export const useFetchInventoryTypesQuery = (params: {
  planPackageId?: number;
  eventId?: number;
}) => {
  return useQuery(
    ["fetchInventoryTypes", params.eventId, params.planPackageId],
    () => {
      return get({
        url: "/tickets/inventory_types",
        params,
      }) as Promise<{ containsStandard: boolean; containsPremium: boolean }>;
    },
    {
      enabled: Boolean(params.eventId && params.planPackageId),
      staleTime: Infinity,
    }
  );
};

export const useFetchPremiumInventoryQuery = (params: {
  planPackageId?: number;
  eventId?: number;
}) => {
  return useQuery(
    ["fetchPremiumInventory", params.eventId, params.planPackageId],
    () => {
      return get({
        url: "/tickets/premium",
        params,
      }) as Promise<PremiumInventoryDetails[]>;
    },
    {
      enabled: Boolean(params.planPackageId) && Boolean(params.eventId),
    }
  );
};

export const holdTicketsRequest = (params: HoldTicketRequest) =>
  patch({
    url: "/tickets/hold",
    params,
  }) as Promise<TicketDetails[]>;

export const reserveTicketsFetcher = (params: {
  accountId: number;
  planPackageId: number;
  eventId: number;
  section: string;
  row: string;
  items: ReserveOrRedeemItem[];
}) => {
  return patch({
    url: "/tickets/reserve",
    params,
  }) as Promise<TicketDetails[]>;
};

export const groupReserveTicketsFetcher = (params: {
  accountId: number;
  planPackageId: number;
  eventId: number | string;
  groupCode: string;
  items: GroupReserveOrRedeemItem[];
}) => {
  return patch({
    url: "/tickets/group_reserve",
    params,
  });
};

export const releaseTicketFetcher = (ticketIds: number[]) => {
  return patch({
    url: "/tickets/release",
    params: {
      ticketIds,
    },
  });
};

export const initiateTransferFetcher = (params: {
  accountId: number;
  groupCodeId: number;
  planPackageComponentType: PlanPackageComponentDetails["type"];
  quantity: number;
}) => {
  return patch({
    url: "/tickets/initiate_transfer",
    params,
  }) as Promise<TransferDetails>;
};

export const useGetTransferDetailsQuery = (params: {
  eventCode: string;
  transferCode: string;
}) => {
  return useQuery<TransferDetails, AxiosError["response"]>(
    ["getTransferDetails"],
    () =>
      get({
        url: "/tickets/transfer",
        params,
      })
  );
};

export const transferTicketFetcher = (params: {
  eventCode: string;
  transferCode: string;
  quantity: number;
}) => {
  return patch({
    url: "/tickets/transfer",
    params,
  });
};

// https://stackoverflow.com/questions/43082934/how-to-execute-promises-sequentially-passing-the-parameters-from-an-array
export const fetchTicketBarcodeByIdsFetcher = (tickets: TicketDetails[]) => {
  return tickets.reduce(
    (promises, ticket) =>
      promises.then((results) =>
        get({
          url: `/tickets/${ticket.id}/barcode`,
        }).then((r: any) => results.concat(r))
      ),
    Promise.resolve([])
  );
};

export const scanTicketsRequest = (ticketIds: number[]) => {
  return post({
    url: "/tickets/scan",
    params: {
      ticketIds,
    },
  });
};

export const useFetchTicketHistoryQuery = () => {
  return useQuery(
    ["fetchTicketHistory"],
    () =>
      get({
        url: "/tickets/account/ticket_history",
      }) as Promise<AccountTicketDetails[]>
  );
};

export const calculateChargeRequest = (params: {
  accountId: number;
  eventId: string | number;
  items: CalculateChargeItem[];
}) =>
  post({
    url: "/tickets/calculate_charge",
    params,
  }) as Promise<TicketCartDetails>;

export const useCalculateGroupChargeQuery = (params: {
  accountId: number | undefined;
  eventId: string | number;
  groupCode: string;
  items: CalculateGroupChargeItem[];
}) => {
  return useQuery(
    ["fetchGroupCharge"],
    () =>
      post({
        url: `/tickets/calculate_group_charge`,
        params,
      }) as Promise<TicketCartDetails>,
    {
      enabled: false,
    }
  );
};

export const useFetchGroupDetailsQuery = (
  params: {
    eventCode: string;
    groupCode: string;
  },
  options?: UseQueryOptions<GroupDetails, unknown, GroupDetails, string[]>
) => {
  return useQuery(
    ["fetchGroupDetails"],
    () =>
      get({
        url: "/tickets/group_code",
        params,
      }) as Promise<GroupDetails>,
    options
  );
};

export const useCalculateRefundQuery = (params: {
  accountId: number;
  ticketIds: number[];
}) => {
  return useQuery(
    ["calculate_refund", params.ticketIds],
    () =>
      post({
        url: "/tickets/calculate_refund",
        params,
      }) as Promise<TicketCartDetails>
  );
};

export const calculateReservationUpdateChargesRequest = (
  params: CalculateReservationUpdateChargesRequest
) =>
  post({
    url: "/tickets/calculate_reservation_update_charges",
    params,
  }) as Promise<ReservationUpdateCartDetails>;

export const holdForUpdateReservationRequest = (
  params: HoldTicketsForReservationUpdateRequest
) =>
  patch({
    url: "/tickets/hold_for_reservation_update",
    params,
  }) as Promise<TicketDetails[]>;

export const reserveForReservationUpdateRequest = (
  params: ReserveTicketsForReservationUpdateRequest
) =>
  patch({
    url: "/tickets/reserve_for_reservation_update",
    params,
  }) as Promise<TicketDetails[]>;
